import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { updatedAllFeaturesResponse } from "@/app/api/v1/accounts/[accountId]/all-calling-feature/utils";
import { sanitizeErrorMessage } from "@/app/schedules/[tn]/ErrorMessageSanitization";
import { TelephoneNumberInfo } from "@/clients/attnow/types";
import { ISPP } from "@/models/Spp";
import { retryLogic } from "@/utils/network";

/**
 * This function is used to get all the TN's for the given accountId/UBID.
 * @param [accountId] - The accountId whose TN's are to be fetched.
 * @return The account details along with all the TN's.
 */
export async function getTNsViaAccountId(accountId: string): Promise<Array<TelephoneNumberInfo>> {
    const res: Response = await fetch(`/api/v1/accounts/${accountId}/tns`);
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

/**
 * This react query hook is used to get all the TN's for the given accountId/UBID.
 * @param [accountId] - The accountId whose TN's are to be fetched.
 */
export function useTNsViaAccountIdQuery(accountId: string) {
    return useQuery({
        queryKey: ["account-tns", accountId],
        queryFn: () => getTNsViaAccountId(accountId),
        retry: retryLogic,
        enabled: !!accountId.length, // This is to prevent the API call from being made when there is no accountId.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}

/**
 * This function is used to get SPP logs for the given accountId/UBID & time period.
 * @param [accountId] - The accountId whose SPP logs are to be fetched.
 * @return The logs with order number
 */
export async function getPendingSppLogs(accountId: string): Promise<ISPP[]> {
    const res: Response = await fetch(`/api/v1/accounts/${accountId}/spp-logs/pendingOrders`);
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

/**
 * This react query hook is used to get SPP logs for the given accountId/UBID & time period.
 * @param [accountId] - The accountId whose SPP logs are to be fetched.
 */
export function useSppLogsPendingQuery(accountId: string) {
    return useQuery({
        queryKey: ["spp-pending-logs", accountId],
        queryFn: () => getPendingSppLogs(accountId),
        retry: retryLogic,
        enabled: !!accountId.length, // This is to prevent the API call from being made when there is no accountId.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}

/**
 * This is the method to delete specific spp order log based on accountId & orderNumber
 * @param accountId - The accountId/UBID whose spp log has to be deleted.
 * @param orderNumber - The orderNumber whose spp log order has to be deleted.
 */
export const deleteSPPPendingOrder = async (accountId: string, orderNumber: string) => {
    const res: Response = await fetch(`/api/v1/accounts/${accountId}/spp-logs/${orderNumber}`, {
        method: "DELETE",
    });
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
};

/**
 * This is a react-query hook that deletes an spp pending log order for a given accountId, orderNumber.
 * @param accountId - The accountId where the order exists and needs to be deleted.
 * @param orderNumber - The orderNumber to be deleted.
 * @return void.
 */
export const useDeleteSPPPendingOrder = (accountId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["spp-pending-logs", accountId],
        mutationFn: async (orderNumber: string) => {
            await deleteSPPPendingOrder(accountId, orderNumber);
        },
        onSuccess: async () => {
            toast.dismiss();
            toast.success("Successfully deleted.");
            await queryClient.invalidateQueries({
                queryKey: ["spp-pending-logs", accountId],
            });
        },
        onError: (err) => {
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
};

/**
 * This function is used to get all the calling features for the given accountId/UBID.
 * @param [accountId] - The accountId whose calling features are to be fetched.
 * @return The customerAccountId with calling feature list.
 */
export async function getCallingFeatures(accountId: string): Promise<updatedAllFeaturesResponse> {
    const res: Response = await fetch(`/api/v1/accounts/${accountId}/all-calling-feature`);
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

/**
 * This react query hook is used to get all the calling features for the given accountId/UBID.
 * @param [accountId] - The accountId whose calling features are to be fetched.
 */
export function useCallingFeaturesQuery(accountId: string) {
    return useQuery({
        queryKey: ["all-calling-features", accountId],
        queryFn: () => getCallingFeatures(accountId),
        retry: retryLogic,
        enabled: !!accountId.length, // This is to prevent the API call from being made when there is no accountId.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}
